import React, { useState } from 'react';
import { FaExclamationTriangle } from 'react-icons/fa';

import { useFormValidation } from '../../hooks/useFormValidation';
import {
    PropertyValidationSchema
} from '../../services/forms/validationSchemas/PropertyValidationSchema';
import { ZeosButton } from '../Shared/ZeosButton';
import { ZeosInput } from '../Shared/ZeosInput';
import { ZeosLabel } from '../Shared/ZeosLabel';
import { ZeosModal } from '../Shared/ZeosModal';
import { ZeosSpinner } from '../Shared/ZeosSpinner';

interface IBinBblFormData {
  bin: string;
  bbl: string;
}

const defaultBinBblForm: IBinBblFormData = {
  bin: "",
  bbl: "",
};

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (bin?: string, bbl?: string) => Promise<void>;
  isLoading?: boolean;
  propertyName?: string;
}

export const BinBblEntryForm = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  propertyName = "this property",
}: IProps) => {
  const { formData, setFormData, errors, formValidate } =
    useFormValidation<IBinBblFormData>(
      PropertyValidationSchema.pick(["bin", "bbl"]),
      defaultBinBblForm
    );

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    await formValidate(async () => {
      setIsSubmitting(true);
      try {
        const bin = formData.bin.trim() || undefined;
        const bbl = formData.bbl.trim() || undefined;
        await onSubmit(bin, bbl);
        onClose();
      } catch (error) {
        console.error("Error updating BIN/BBL:", error);
      } finally {
        setIsSubmitting(false);
      }
    });
  };

  const handleChange = (field: keyof IBinBblFormData, value: string) => {
    // Limit input length based on field type
    const maxLength = field === "bin" ? 7 : 10;
    const trimmedValue = value.slice(0, maxLength);
    setFormData((prev) => ({ ...prev, [field]: trimmedValue }));
  };

  return (
    <ZeosModal isOpen={isOpen} onClose={onClose}>
      <div className="p-6 max-w-md mx-auto">
        <div className="flex items-center mb-4">
          <FaExclamationTriangle className="text-zeos-orange-dk mr-3 text-xl" />
          <h3 className="text-xl font-semibold text-zeos-black">
            Property Information Required
          </h3>
        </div>

        <p className="text-zeos-black mb-6">
          To provide you with the most accurate energy insights and compliance
          information for <span className="font-semibold">{propertyName}</span>,
          we need your property's BIN (Building Identification Number) and/or
          BBL (Borough, Block, Lot) information.
        </p>

        <div className="space-y-4">
          <div>
            <ZeosLabel
              label="BIN (Building Identification Number)"
              htmlFor="bin"
              required={false}
            />
            <ZeosInput
              name="bin"
              type="text"
              value={formData.bin}
              onChange={(e) => handleChange("bin", e.target.value)}
              placeholder="Enter 7-digit BIN (optional)"
              error={errors.bin}
            />
            <p className="text-sm text-gray-600 mt-1">
              Found on your property tax bill or NYC DOB website
            </p>
          </div>

          <div>
            <ZeosLabel
              label="BBL (Borough, Block, Lot)"
              htmlFor="bbl"
              required={false}
            />
            <ZeosInput
              name="bbl"
              type="text"
              value={formData.bbl}
              onChange={(e) => handleChange("bbl", e.target.value)}
              placeholder="Enter 10-digit BBL (optional)"
              error={errors.bbl}
            />
            <p className="text-sm text-gray-600 mt-1">
              Found on your property tax bill or NYC DOB website
            </p>
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <ZeosButton
            label="Skip for Now"
            type="button"
            mode="prev"
            onPress={onClose}
          />
          <ZeosButton
            label={isSubmitting ? "Saving..." : "Save Information"}
            type="button"
            mode="action"
            onPress={handleSubmit}
            isDisabled={isSubmitting || isLoading}
          />
        </div>

        {isSubmitting && (
          <div className="flex items-center justify-center mt-4">
            <ZeosSpinner />
          </div>
        )}
      </div>
    </ZeosModal>
  );
};
